// ── 상황판 관리자 메뉴 위젯 — 큰 카드 디자인 통일 (2026-06-07) ───────────────
//
// UnifiedCard 와 동일한 큰 카드 패턴:
//   - 카드 배경: 흰색 (CARD_BG)
//   - 테두리: border + border-{accent}-100 (옅음)
//   - 상단 강조: border-t-[3px] border-t-{accent}-400
//   - 아이콘 색상: spec.accent 텍스트 (accentText)
//   - 라벨: text-[12px] font-bold text-[var(--text-sub)]
//   - 값: text-xs font-black tabular-nums + accent text (사용자 명세 — 작게 통일)
//   - hover: brightness-95
//   - 모든 위젯 col-span-2 (모바일 2열, 데스크탑 6열에서 3개/줄)
//   - spec.monthNavigate === true → 양옆 chevron 으로 월 이동
//
// render() 가 ReactNode 반환 시 그 안의 색상은 spec 가 직접 통제. 단순 문자열 반환 시 accent.
//
// 드래그 정렬 — DndContext + SortableContext 로 admin 위젯끼리 자유 드래그.

import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  DndContext, closestCenter,
  PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, rectSortingStrategy, useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '../../../lib/utils';
import { ADMIN_WIDGET_SPECS, getAdminWidgetSpec, hasAdminSpec } from './AdminWidgetsSpec';
import {
  accentText,
  CARD_BG, DEFAULT_ACCENT,
} from './widgetAccent';
import { reorderByPreference } from './useSituationWidgets';

// 월 이동 헬퍼 — YYYY-MM ± n
function shiftMonth(ym, delta) {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return ym;
  const [y, m] = ym.split('-').map(Number);
  const zb = (y * 12) + (m - 1) + delta;
  const ny = Math.floor(zb / 12);
  const nm = (zb % 12) + 1;
  return `${ny}-${String(nm).padStart(2, '0')}`;
}

// ── 큰 카드 패턴 위젯 카드 (개별) ─────────────────────────────────────────────
// month / onMonthChange 는 spec.monthNavigate === true 인 위젯만 사용.
// accentOverride: 사용자가 위젯 설정 모달에서 직접 지정한 색상 (preference 우선)
// sortableId: 드래그 정렬용 id (없으면 비활성)
// onClick: 짧은 클릭 시 navigate (2026-06-16 사용자 명세). 없으면 cursor-default.
export function AdminWidgetCard({ code, data, loading, month, onMonthChange, accentOverride, sortableId, onClick }) {
  // 정적 spec 우선, 없으면 통합 위젯 prefix 동적 spec (data 의 menuName 사용)
  const spec = getAdminWidgetSpec(code, data);
  // hook 순서 유지 — sortable 은 spec 유무와 무관하게 항상 호출.
  const sortable = useSortable({ id: sortableId || `__nosort_${code || 'x'}`, disabled: !sortableId });
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = sortable;

  if (!spec) return null;
  const { Icon, label } = spec;
  const monthNav = spec.monthNavigate === true;
  const accentKey = accentOverride || spec.accent || DEFAULT_ACCENT;
  const txt = accentText(accentKey);

  // size 분기 (사용자 명세 — 모든 row 같은 높이 220px 기준):
  //   S = col-span-1 row-span-1 (220×220 정사각)
  //   M = col-span-2 row-span-1 (460×220 가로 직사각)
  //   L = col-span-2 row-span-2 (460×460 정사각)
  const size = spec.size || 'M';
  const cellSpan = size === 'S'
    ? 'col-span-1 row-span-1'
    : size === 'L'
      ? 'col-span-2 row-span-2'
      : 'col-span-2 row-span-1';

  const dragStyle = sortableId
    ? { transform: CSS.Transform.toString(transform), transition }
    : undefined;

  const rendered = (() => {
    if (loading) return '…';
    if (!data) return '-';
    if (data.error) return '오류';
    try {
      return spec.render(data);
    } catch (_e) {
      return '-';
    }
  })();

  const handlePrev = (e) => {
    e.stopPropagation();
    if (!monthNav || !onMonthChange) return;
    onMonthChange(shiftMonth(month || (data && data.month) || '', -1));
  };
  const handleNext = (e) => {
    e.stopPropagation();
    if (!monthNav || !onMonthChange) return;
    onMonthChange(shiftMonth(month || (data && data.month) || '', 1));
  };

  // 사용자 명세 (2026-06-08): 모든 사이즈(S/M/L) 통일 layout —
  //   헤더 (icon + 라벨 + chevron) 위 / 본문 아래 + 우측 하단 watermark.
  //   M 도 stacked 적용해 라벨이 본문과 같은 줄에 잘리지 않게.
  const isS = size === 'S';
  const isL = size === 'L';
  const isStacked = true; // S/M/L 모두 헤더 위 / 본문 아래 통일
  const Watermark = spec.watermarkIcon || Icon;
  // S 패딩 px-4 py-4, M/L 패딩 px-4 py-3
  const padCls = isS ? 'px-4 py-4' : 'px-4 py-3';
  // watermark 크기 — S=88 / L=100 (큰 영역) / M=80
  const watermarkSize = isL ? 100 : isS ? 88 : 80;

  // 짧은 클릭 시 onClick(navigate). dnd-kit delay sensor 가 짧은 클릭은 drag 활성화 X.
  return (
    <div
      ref={sortableId ? setNodeRef : undefined}
      style={dragStyle}
      onClick={(onClick && !isDragging) ? onClick : undefined}
      {...(sortableId ? attributes : {})}
      {...(sortableId ? listeners : {})}
      className={cn(
      cellSpan,
      // 2026-08-25 재스킨(사용자 명세): 색 띠·색 테두리 제거 — 중립 테두리+둥근 모서리+호버 그림자.
      // accent 는 아이콘·수치·워터마크 색에만.
      'rounded-2xl border border-[var(--border)] min-w-0 transition-shadow relative overflow-hidden',
      padCls,
      isStacked ? 'flex flex-col justify-between h-full' : 'flex items-start gap-2',
      CARD_BG, 'hover:shadow-[0_4px_14px_rgba(0,0,0,0.08)]',
      (onClick || sortableId) ? 'cursor-pointer active:cursor-grabbing' : 'cursor-default',
      sortableId && isDragging && 'opacity-50 shadow-lg z-10 ring-2 ring-blue-400',
    )}>
      {/* watermark — 우측 하단, 옅게 (S/L 사이즈) */}
      {isStacked && Watermark && (
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute -bottom-2 -right-2 opacity-10',
            txt,
          )}
        >
          <Watermark size={watermarkSize} strokeWidth={2} />
        </span>
      )}

      {/* 헤더 — S/L 는 상단 / M 은 한 줄 좌측 */}
      <div className={cn(
        'relative z-[1] flex items-center gap-1.5 min-w-0',
        isStacked && 'w-full',
      )}>
        {Icon && (
          <span className={cn('flex-shrink-0', txt)}>
            <Icon size={16} strokeWidth={2.25} />
          </span>
        )}
        <span className="text-[12px] font-bold text-[var(--text-sub)] whitespace-nowrap">{label}</span>

        {monthNav && (
          <button
            type="button"
            aria-label="이전 달"
            onClick={handlePrev}
            onPointerDown={(e) => e.stopPropagation()}
            className="shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-sub)]"
          >
            <ChevronLeft size={12} />
          </button>
        )}
        {monthNav && (
          <span className="text-[10px] text-[var(--text-sub)] shrink-0 tabular-nums">
            {spec.monthLabel ? spec.monthLabel(month || (data && data.month) || '') : (month || (data && data.month) || '')}
          </span>
        )}
        {monthNav && (
          <button
            type="button"
            aria-label="다음 달"
            onClick={handleNext}
            onPointerDown={(e) => e.stopPropagation()}
            className="shrink-0 p-0.5 rounded hover:bg-black/5 dark:hover:bg-white/5 text-[var(--text-sub)]"
          >
            <ChevronRight size={12} />
          </button>
        )}

        {/* M 한 줄: 우측 값 영역 (S/L 에서는 ml-auto 비활성) */}
        {!isStacked && (
          <div className={cn(
            'ml-auto flex items-baseline gap-1 justify-end min-w-0',
            'text-xs font-black tabular-nums',
            loading ? 'text-[var(--text-muted)]' : txt,
          )}>
            {rendered}
          </div>
        )}
      </div>

      {/* S 본문: 큰 값 (중앙 정렬) — spec.render 결과 */}
      {isS && (
        <div className={cn(
          'relative z-[1] flex-1 flex flex-col justify-center items-end min-w-0 mt-2',
          loading ? 'text-[var(--text-muted)]' : txt,
        )}>
          {rendered}
        </div>
      )}

      {/* M 본문: 헤더 아래 펼친 영역 (상단 정렬) — spec.render 결과 */}
      {!isS && !isL && (
        <div className={cn(
          'relative z-[1] flex-1 flex flex-col items-end min-w-0 mt-2 overflow-hidden',
          loading ? 'text-[var(--text-muted)]' : txt,
        )}>
          {rendered}
        </div>
      )}

      {/* L 본문: 펼친 영역 (상단 정렬) — spec.render 결과 (큰 강조 폰트는 spec 가 적용) */}
      {isL && (
        <div className={cn(
          'relative z-[1] flex-1 flex flex-col items-end min-w-0 mt-2 overflow-hidden',
          loading ? 'text-[var(--text-muted)]' : txt,
        )}>
          {rendered}
        </div>
      )}
    </div>
  );
}

// ── 가시 위젯 그리드 ──────────────────────────────────────────────────────────
// 데스크탑 6열, 모바일 2열 (모든 위젯 col-span-2).
// monthMap: { code: 'YYYY-MM' } — 위젯별 월 상태.
// onMonthChange(code, newMonth): 카드 chevron 클릭 시 호출.
// order: 사용자 드래그 순서 (없으면 visibleCodes 원순서)
// onOrderChange(newCodes): 드래그 후 호출 — admin 위젯끼리만 순서.
export function AdminWidgetsGrid({
  visibleCodes = [], data = {}, loading = false, className,
  monthMap = {}, onMonthChange, accentMap = {},
  order = [], onOrderChange,
}) {
  // sensors — 사용자 명세 (2026-06-16): 짧은 클릭 = onClick / 500ms+ 누름 = 드래그.
  //   PointerSensor / TouchSensor 모두 delay 500ms 통일.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { delay: 500, tolerance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 500, tolerance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const codes = (visibleCodes || []).filter((c) => hasAdminSpec(c));
  const orderedCodes = reorderByPreference(codes, order);
  if (orderedCodes.length === 0) return null;

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    if (!onOrderChange) return;
    const oldIndex = orderedCodes.indexOf(active.id);
    const newIndex = orderedCodes.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const next = [...orderedCodes];
    const [moved] = next.splice(oldIndex, 1);
    next.splice(newIndex, 0, moved);
    onOrderChange(next);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={orderedCodes} strategy={rectSortingStrategy}>
        <div className={cn(
          'grid gap-2',
          // 정사각 비율 그리드 — 모바일 row 175px / 데스크탑(lg+) 220px
          'grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 auto-rows-[175px] lg:auto-rows-[220px]',
          className,
        )}>
          {orderedCodes.map((code) => (
            <AdminWidgetCard
              key={code}
              code={code}
              data={data[code]}
              loading={loading}
              month={monthMap[code]}
              onMonthChange={onMonthChange ? (m) => onMonthChange(code, m) : undefined}
              accentOverride={accentMap[code]}
              sortableId={code}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export { ADMIN_WIDGET_SPECS };
export const ADMIN_WIDGET_CODES = Object.keys(ADMIN_WIDGET_SPECS);
